// src/components/StudentNotesModal.js
import React, { useState } from 'react';
import {
  Modal,
  Button,
  Table,
  Form,
  Spinner,
  Dropdown,
} from 'react-bootstrap';

const StudentNotesModal = ({ show, onHide, notesApi }) => {
  const {
    notesStudent,
    notes = [],
    notesLoading,
    addNote,
    saveNote,
    deleteNote,
    editingNoteId,
    setEditingNoteId,
    editingNoteText,
    setEditingNoteText,
    noteToDelete,
    setNoteToDelete,
  } = notesApi || {};

  const [newNoteText, setNewNoteText] = useState('');

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!notesStudent) return;
    if (!newNoteText.trim()) return;
    await addNote(newNoteText);
    setNewNoteText('');
  };

  const handleSave = async () => {
    await saveNote();
  };

  const handleDelete = async () => {
    await deleteNote();
  };

  const bodyStyle = {
    maxHeight: '65vh',
    overflowY: 'auto',
  };

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          setNewNoteText('');
          onHide();
        }}
        centered
        fullscreen="md-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {notesStudent
              ? `Notes — ${notesStudent.first_name} ${notesStudent.last_name}`
              : 'Notes'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={bodyStyle}>
          {/* Add form */}
          <Form
            onSubmit={handleAdd}
            className="d-flex flex-wrap gap-2 mb-3"
          >
            <Form.Control
              placeholder="Add a note"
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              className="flex-grow-1"
            />
            <Button type="submit" variant="success" className="flex-shrink-0">
              Add
            </Button>
          </Form>

          {notesLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : notes.length === 0 ? (
            <p className="text-muted mb-0">No notes yet.</p>
          ) : (
            <>
              {/* Desktop / tablet */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <Table bordered responsive size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 180 }}>Note</th>
                        <th>Teacher</th>
                        <th>Created</th>
                        <th style={{ width: 120 }} className="text-end">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {notes.map((n) => (
                        <tr key={n.id}>
                          <td>
                            {editingNoteId === n.id ? (
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                              />
                            ) : (
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {n.note_text}
                              </div>
                            )}
                          </td>
                          <td>
                            <small>{n.author_name || 'Unknown'}</small>
                          </td>
                          <td>
                            <small>
                              {n.created_at
                                ? new Date(n.created_at).toLocaleString()
                                : ''}
                            </small>
                          </td>
                          <td className="text-end">
                            {editingNoteId === n.id ? (
                              <>
                                <Button size="sm" onClick={handleSave}>
                                  Save
                                </Button>{' '}
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => {
                                    setEditingNoteId(null);
                                    setEditingNoteText('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  size="sm"
                                  variant="outline-secondary"
                                >
                                  Actions
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setEditingNoteId(n.id);
                                      setEditingNoteText(n.note_text);
                                    }}
                                  >
                                    Edit
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() =>
                                      setNoteToDelete({
                                        id: n.id,
                                        text: n.note_text,
                                      })
                                    }
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Mobile cards */}
              <div className="d-md-none">
                {notes.map((n) => {
                  const isEditing = editingNoteId === n.id;
                  return (
                    <div key={n.id} className="mb-3 p-3 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div className="fw-semibold" style={{ maxWidth: '70%' }}>
                          {n.author_name || 'Unknown'}
                        </div>
                        <small className="text-muted">
                          {n.created_at
                            ? new Date(n.created_at).toLocaleDateString()
                            : ''}
                        </small>
                      </div>

                      {isEditing ? (
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="mb-2"
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          placeholder="Note"
                        />
                      ) : (
                        <div className="small mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {n.note_text}
                        </div>
                      )}

                      <div className="d-flex gap-2">
                        {isEditing ? (
                          <>
                            <Button size="sm" className="flex-fill" onClick={handleSave}>
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="flex-fill"
                              onClick={() => {
                                setEditingNoteId(null);
                                setEditingNoteText('');
                              }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              className="flex-fill"
                              onClick={() => {
                                setEditingNoteId(n.id);
                                setEditingNoteText(n.note_text);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="flex-fill"
                              onClick={() =>
                                setNoteToDelete({
                                  id: n.id,
                                  text: n.note_text,
                                })
                              }
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* confirm delete */}
      <Modal show={!!noteToDelete} onHide={() => setNoteToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete note?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this note?
          <div className="mt-2">
            <em>{noteToDelete?.text}</em>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setNoteToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StudentNotesModal;
